
import { defineComponent, ref, computed } from "vue";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import AddCustomerModal from "@/components/modals/forms/AddAdmissionsApplication.vue";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import axios from "@/axios-instance"
import Permission from "@/core/config/Permission";
import { useRouter } from "vue-router";
import { checkRoleIsSuperAdmin } from "@/router";
import { emitter } from "@/main";
import { hideModal } from "@/core/helpers/dom";
import ApplicantQuickDrawer from "@/components/customers/ApplicantQuickDrawer.vue";
import { printUuDaiHocBong } from "@/core/helpers/printUuDaiHocBong";

interface DataApplicant {
    createdAt: string;
    id: number;
    phase: number;
    profileCode: string;
    major: string;
    majorCode?: string;
    status: string;
    studentId: string;
}
export default defineComponent({
    name: "statement",
    data() {
        return {
            dataAdmissionRegister: {},
            //ca "hủy nhầm phiếu nhập học đã chốt" (GET applicant/wrong-cancel/:idStudent)
            wrongCancel: null as any,
            //ca đã xử theo hướng "chấp nhận phiếu mới" -> chỉ còn ghi chú mờ
            wrongCancelResolved: null as any,
            isSuperAdmin: checkRoleIsSuperAdmin(),
        }
    },
    watch: {
        dataApplicant: {
            immediate: true,
            handler() { this.loadWrongCancel() },
        },
    },
    emits: ["callbackFunction"],
    props: {
        cardClasses: String,
        dataApplicant: { type: Array as () => Array<DataApplicant> },
        // candidateListDetail truyền sẵn — dùng làm email fallback cho Gửi báo phí
        dataCustomer: { type: Object, default: null },
    },
    components: {
        Datatable,
        AddCustomerModal,
        ApplicantQuickDrawer,
    },
    methods: {
        // In phiếu xác nhận ưu đãi/học bổng ngay tại chi tiết ứng viên: fetch applicant theo profileCode,
        // in bằng helper dùng chung (printUuDaiHocBong). Có cả HB + ưu đãi -> hỏi in phiếu nào.
        async printUuDai(customer) {
            try {
                const { data: applicant } = await axios.get(`applicant/profile/${customer.profileCode}`)
                if (!applicant || !applicant.id) {
                    Swal.fire({ text: 'Không tìm thấy phiếu nhập học cho mã hồ sơ này.', icon: 'warning', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
                    return
                }
                const hocbong = Array.isArray(applicant.freeTuitionRegistration)
                    ? applicant.freeTuitionRegistration.find((i: any) => i.registrationType == 'scholarships') : null
                const uudai = applicant.orderDetails?.promotion
                if (!hocbong && !uudai) {
                    Swal.fire({ text: 'Hồ sơ chưa có ưu đãi hoặc học bổng để in phiếu.', icon: 'info', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
                    return
                }
                if (hocbong && uudai) {
                    // SweetAlert2 v9 KHÔNG có showDenyButton (v10+) -> dùng cancel làm nút "Ưu đãi",
                    // phân biệt với ESC/click-ngoài qua DismissReason.cancel; đóng thật bằng nút X
                    const res = await Swal.fire({
                        text: 'Hồ sơ có cả học bổng và ưu đãi — in phiếu nào?',
                        icon: 'question',
                        buttonsStyling: false,
                        showCancelButton: true,
                        showCloseButton: true,
                        confirmButtonText: 'In phiếu Học bổng',
                        cancelButtonText: 'In phiếu Ưu đãi',
                        customClass: { confirmButton: 'btn btn-primary order-2', cancelButton: 'btn btn-light-primary order-1' },
                    })
                    if (res.isConfirmed) await printUuDaiHocBong(applicant, hocbong)
                    else if (res.dismiss === Swal.DismissReason.cancel) await printUuDaiHocBong(applicant, uudai)
                    return
                }
                await printUuDaiHocBong(applicant, hocbong || uudai)
            } catch (e: any) {
                Swal.fire({ text: e?.response?.data?.message || 'Không tải được thông tin để in phiếu.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
            }
        },
        // Click MÃ HỒ SƠ -> drawer xem nhanh phiếu nhập học (component chung với Danh sách nhập học);
        // drawer tự fetch applicant theo profileCode
        openApplicantQuickView(customer) {
            if (!customer || customer.status === 'none') return // chưa khởi tạo -> chưa có phiếu nhập học
            ;(this.$refs.applicantQuickDrawer as any)?.open(customer.profileCode)
        },
        rowToDetail(customer){
            // Click vào dòng -> Chi tiết hồ sơ nhập học (bỏ qua hồ sơ chưa khởi tạo status=none, chưa có applicant)
            if(!customer || customer.status === 'none') return
            this.fintApplicentDetail(customer.profileCode)
        },
        async fintApplicentDetail(code){
            const applicant = await axios.get(`applicant/profile/${code}`)
            if(applicant && applicant.data && applicant.data.id){
                this.router.push({ path: `/danhsachthutucnhaphoc/${applicant.data.id}`})
            }
        },
        // "In phiếu tiếp nhận hồ sơ": deep-link trang chi tiết nhập học mở SẴN tab Phiếu tiếp nhận
        // (form tick giấy tờ đã nhận rồi in — cần ngữ cảnh đầy đủ, không nhúng modal nhân đôi component)
        async gotoReceiptTicket(customer){
            const applicant = await axios.get(`applicant/profile/${customer.profileCode}`).catch(() => null)
            if(applicant && applicant.data && applicant.data.id){
                this.router.push({ path: `/danhsachthutucnhaphoc/${applicant.data.id}`, hash: '#phieutiepnhan' })
            }
        },
        // Gửi email báo phí — hộp thoại xác nhận + cho đổi email (đồng bộ sendMailStudent của trang chi tiết nhập học)
        async sendFeeMail(customer){
            const applicant = await axios.get(`applicant/profile/${customer.profileCode}`).catch(() => null)
            if(!applicant || !applicant.data || !applicant.data.id){
                Swal.fire({ text: 'Chưa có phiếu nhập học của hồ sơ này.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-light-danger' } })
                return
            }
            const applicantId = applicant.data.id
            const defaultEmail = applicant.data.contactEmail || (this as any).dataCustomer?.email || ''
            Swal.fire({
                title: 'Gửi báo phí',
                html: `Email ứng viên nhận báo học phí?`,
                input: 'email',
                inputValue: defaultEmail,
                inputPlaceholder: 'Email nhận báo phí',
                icon: 'question',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Đồng ý',
                cancelButtonText: 'Trở lại',
                customClass: { confirmButton: 'btn btn-primary order-2', cancelButton: 'btn btn-light order-1' },
                inputValidator: (value) => {
                    if (!value) return 'Vui lòng nhập email nhận báo phí'
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Email không hợp lệ'
                    return null
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.post(`applicant/mail/sendmailhoantatnhaphoc`, { applicantids: [applicantId], overrideEmail: result.value })
                    .then(({ data }) => {
                        Swal.fire({
                            text: `Đã gửi mail ${data[0]?.status == 'success' ? 'thành công' : 'thất bại'} đến ${result.value}!`,
                            icon: data[0]?.status == 'success' ? 'success' : 'error',
                            buttonsStyling: false,
                            confirmButtonText: 'Đóng',
                            customClass: { confirmButton: 'btn btn-primary' },
                        })
                    })
                }
            })
        },
        //id ỨNG VIÊN (students.id) của trang đang mở. Lấy nhiều nguồn vì mỗi nơi gọi component đặt
        //tên khác nhau: row winner trả `idStudent`, prop dataCustomer là bản ghi ứng viên, và route
        ///danhsachungvien/:id chính là idStudent (WidgetComment cũng dùng nguồn này).
        idStudentOfPage(): string | null {
            const row: any = this.dataApplicant?.[0]
            return row?.studentId || row?.idStudent || this.dataCustomer?.id || (this as any).$route?.params?.id || null
        },
        //Ứng viên có dính ca "hủy nhầm phiếu đã chốt" không (di sản Mở khóa hồ sơ thời chưa có gate).
        //CHỈ ĐỌC — chạy nền, lỗi thì im lặng (banner không hiện, mọi thứ khác vẫn dùng bình thường).
        async loadWrongCancel() {
            const idStudent = this.idStudentOfPage()
            if (!idStudent) { this.wrongCancel = null; this.wrongCancelResolved = null; return }
            try {
                const { data } = await axios.get(`applicant/wrong-cancel/${idStudent}`)
                this.wrongCancel = data?.broken ? data : null
                this.wrongCancelResolved = (!data?.broken && data?.resolved) ? data : null
            } catch (e) {
                this.wrongCancel = null
                this.wrongCancelResolved = null
            }
        },
        //Giải thích 2 hướng xử lý — nội dung phải khớp BE thật (restoreWrongCancel / acceptWrongCancel),
        //sửa logic bên BE thì cập nhật đây.
        showWrongCancelInfo() {
            const w = this.wrongCancel || {}
            Swal.fire({
                title: 'Bấm nút nào thì chuyện gì xảy ra?',
                width: 720,
                html: `<div class="text-start fs-7 umt-info-body">
                    <div class="mb-2"><b class="text-danger">Khôi phục phiếu cũ</b> — dùng khi phiếu mới bị mở NHẦM.</div>
                    <ul class="ps-4 mb-3 text-gray-700">
                        <li>Phiếu <b>${w.cancelled?.label || 'cũ'}</b> quay lại trạng thái <b>Hoàn tất nhập học</b>, hồ sơ trúng tuyển của nó trở lại <b>Chọn nhập học</b>.</li>
                        <li>Phiếu <b>${w.current?.label || 'mới'}</b> bị <b class="text-danger">xóa vĩnh viễn</b>, hồ sơ trúng tuyển của nó về <i>Đã khóa</i> (mở lại được sau).</li>
                        <li>MSSV và email trường <b>giữ nguyên</b> — vẫn là một sinh viên.</li>
                        <li>Chỉ bấm được khi phiếu mới <b>chưa xác nhận hoàn tất, chưa chuyển giao SIS và không giữ biên lai</b>.</li>
                        <li>Xong rồi: kiểm tra lại công nợ (học phí 2 ngành có thể khác) và bấm <b>Đồng bộ SIS</b>.</li>
                    </ul>
                    <div class="mb-2"><b class="text-primary">Chấp nhận phiếu mới</b> — dùng khi việc đổi ngành là ĐÚNG, hoặc phiếu mới đã đi quá xa để quay lại.</div>
                    <ul class="ps-4 mb-3 text-gray-700">
                        <li><b>Không đổi bất kỳ dữ liệu nào</b> — chỉ đóng cảnh báo này và mở lại nút <i>Xác nhận hoàn tất</i>.</li>
                        <li>Ghi vết ai chấp nhận, khi nào, vì sao vào <b>Ghi chú</b> của ứng viên.</li>
                    </ul>
                    <div class="umt-info-warn rounded p-3 mt-3 text-gray-700">
                        <div class="fw-bold mb-1 text-gray-800">Chấp nhận rồi vẫn còn 2 việc phải làm tay</div>
                        <ul class="ps-4 mb-0">
                            <li>Nhờ <b>Phòng Đào tạo đổi ngành bản SINH VIÊN trên SIS</b> — SIS đã chuyển hồ sơ thành sinh viên theo ngành cũ và <b>không tự đổi</b> theo phiếu mới.</li>
                            <li><b>Đối soát biên lai / công nợ</b> giữa 2 phiếu: tiền đã thu có thể còn nằm ở phiếu đã hủy.</li>
                        </ul>
                    </div>
                    <div class="mt-3 text-muted">Cả 2 nút đều <b>bắt buộc ghi lý do</b>.</div>
                </div>`,
                buttonsStyling: false,
                confirmButtonText: 'Đã hiểu',
                customClass: { popup: 'umt-info-popup', confirmButton: 'btn btn-primary' },
            })
        },
        //Nhánh B (gặp nhiều hơn): GIỮ phiếu mới, đi tiếp. Không đụng dữ liệu — chỉ đóng cảnh báo + ghi vết.
        //Phần SIS (đổi ngành bản sinh viên) và đối soát biên lai vẫn phải làm tay, message BE nhắc lại.
        async acceptWrongCancel() {
            const w = this.wrongCancel
            if (!w) return
            const idStudent = this.idStudentOfPage()
            const res = await Swal.fire({
                title: 'Chấp nhận phiếu mới',
                html: `Giữ phiếu <b>${w.current?.label || ''}</b> làm hồ sơ nhập học chính thức và đóng cảnh báo này.`
                    + `<br/><span class="text-muted fs-7">SIS vẫn đang ghi ngành của phiếu cũ — phải nhờ Phòng Đào tạo đổi ngành bản sinh viên, và đối soát biên lai giữa 2 phiếu.</span>`,
                icon: 'question',
                input: 'textarea',
                inputPlaceholder: 'Căn cứ: đơn đổi ngành đã duyệt, SV đã học ngành mới…',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Chấp nhận',
                cancelButtonText: 'Trở lại',
                customClass: { confirmButton: 'btn btn-primary order-2', cancelButton: 'btn btn-light order-1' },
                inputValidator: (value) => (!value || !value.trim()) ? 'Vui lòng ghi lý do chấp nhận' : null,
            })
            if (!res.isConfirmed) return
            this.popupLoading()
            try {
                const { data } = await axios.put(`applicant/wrong-cancel/accept/${idStudent}`, { reason: res.value })
                Swal.close()
                await this.loadWrongCancel()
                emitter.emit("updateApplicantData")
                Swal.fire({ text: data?.message || 'Đã chấp nhận phiếu mới.', icon: 'success', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
            } catch (e: any) {
                Swal.close()
                Swal.fire({
                    title: 'Không xử lý được',
                    text: e?.response?.data?.errorMessage || e?.response?.data?.message || 'Có lỗi xảy ra. Vui lòng thử lại.',
                    icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng',
                    customClass: { confirmButton: 'btn btn-primary' },
                })
            }
        },
        //Khôi phục (CHỈ Superadmin, BE gác bằng requireSuperAdmin): trả phiếu đã chốt về "Hoàn tất nhập
        //học" + xóa phiếu mở nhầm. Bắt buộc ghi lý do (ai báo lên, vì sao) — ghi [TIMELINE].
        async restoreWrongCancel() {
            const w = this.wrongCancel
            if (!w) return
            const idStudent = this.idStudentOfPage()
            const res = await Swal.fire({
                title: 'Khôi phục phiếu đã hủy nhầm',
                html: `Trả phiếu <b>${w.cancelled?.label || ''}</b> về <b>Hoàn tất nhập học</b>`
                    + `${w.cancelled?.studentCode ? ` (MSSV ${w.cancelled.studentCode})` : ''}`
                    + `<br/>và <b class="text-danger">XÓA</b> phiếu mở nhầm <b>${w.current?.label || ''}</b>.`
                    + `<br/><span class="text-muted fs-7">Thao tác không hoàn tác được.</span>`,
                icon: 'warning',
                input: 'textarea',
                inputPlaceholder: 'Lý do khôi phục: ai báo lên, vì sao…',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Khôi phục',
                cancelButtonText: 'Trở lại',
                customClass: { confirmButton: 'btn btn-danger order-2', cancelButton: 'btn btn-light order-1' },
                inputValidator: (value) => (!value || !value.trim()) ? 'Vui lòng ghi lý do khôi phục' : null,
            })
            if (!res.isConfirmed) return
            this.popupLoading()
            try {
                const { data } = await axios.put(`applicant/wrong-cancel/restore/${idStudent}`, { reason: res.value })
                Swal.close()
                await this.loadWrongCancel()
                emitter.emit("updateApplicantData")
                Swal.fire({ text: data?.message || 'Đã khôi phục phiếu nhập học.', icon: 'success', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
            } catch (e: any) {
                Swal.close()
                Swal.fire({
                    title: 'Không khôi phục được',
                    text: e?.response?.data?.errorMessage || e?.response?.data?.message || 'Có lỗi xảy ra. Vui lòng thử lại.',
                    icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng',
                    customClass: { confirmButton: 'btn btn-primary' },
                })
            }
        },
        async getTuition(majorCode: any) {
            await axios.get(`/setting/tuition/?majorCode=${majorCode}`)
            .then(( {data} ) => {
                // this.dataTuition = data
                let tempData = data.filter((i: any) => i.majorCode == majorCode)
                // console.log('tempData',tempData)
                this.optionsPrograms = tempData.map((i: any) => ({
                    label: i.programtype == 'TIENG_VIET' ? 'Tiếng Việt' : 'Song ngữ',
                    value: i.programtype,
                }))
            })
            .catch(({ response }) => {
                console.log('err',response)
            });
        },
        callModalAddApp(e) {
            e.preventDefault()
            this.kt_modal_lock_ad?.click()
            this.formData.programtype = 'TIENG_VIET'
            //mở lại modal thì xóa lý do/lỗi của lần trước (tránh gửi nhầm lý do hồ sơ khác)
            this.formData.reason = ''
            this.formData.reasonError = ''
            // this.resetDataForm()
        },
        async unlockProfile(){
            const reason = (this.formData.reason || '').trim()
            if (!reason) {
                this.formData.reasonError = 'Vui lòng ghi lý do đổi hồ sơ nhập học'
                return
            }
            let tempData = {
                profileCode: this.formData.profileCode,
                programtype: this.formData.programtype,
                reason,
            }
            await axios.post(`applicant/unlocked`,tempData)
            .then(({ data }) => {
                emitter.emit("updateApplicantData");
                hideModal(this.lockAdModalRef);
                Swal.fire({ text: data?.status || 'Đã đổi hồ sơ nhập học thành công!', icon: 'success', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
            })
            .catch(({ response }) => {
                //BE chặn hồ sơ đã xác nhận hoàn tất / đã chuyển giao SIS / SIS đã thành SV -> phải hiện
                //cho người dùng thấy, trước đây chỉ console.log nên modal đứng im như không có gì xảy ra
                console.log('err', response)
                Swal.fire({
                    title: 'Không đổi được hồ sơ nhập học',
                    text: response?.data?.errorMessage || response?.data?.message || 'Có lỗi xảy ra. Vui lòng thử lại.',
                    icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng',
                    customClass: { confirmButton: 'btn btn-primary' },
                })
            });

        },
        popupLoading() {
            Swal.fire({
                html: "<div class='custom-loading'><span>Đang xử lý. Vui lòng chờ trong giây lát!!!</span> <span class='loader'></span></div>",
                buttonsStyling: false,
                timerProgressBar: true,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                showConfirmButton: false,
            }).then(() => {
                //
            });
        },
        async getPdf(record) {
            // Tab Hồ sơ nhập học -> IN PHIẾU THÔNG TIN SINH VIÊN (đúng ngữ cảnh nhập học).
            // Resolve profileCode -> applicant id (như fintApplicentDetail) rồi mở /phieuthongtinsinhvien?id= (render trực tiếp).
            const profileCode = record?.profileCode || record?.code
            if (!profileCode) {
                Swal.fire({ text: 'Không tìm thấy mã hồ sơ để in.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-light-danger' } })
                return
            }
            this.popupLoading()
            await axios.get(`applicant/profile/${profileCode}`)
                .then(({ data }) => {
                    Swal.close()
                    if (!data || !data.id) {
                        Swal.fire({ text: 'Chưa có hồ sơ nhập học để in phiếu thông tin sinh viên.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-light-danger' } })
                        return
                    }
                    window.open(`/phieuthongtinsinhvien?id=${data.id}&autoprint=1`, '_blank')
                })
                .catch(({ response }) => {
                    Swal.close()
                    console.log('err', response)
                    Swal.fire({ text: 'Không in được phiếu thông tin sinh viên. Vui lòng thử lại.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-light-danger' } })
                });
        },
        async exportPdf(record) {
            // Tab Hồ sơ nhập học -> XUẤT FILE PDF phiếu thông tin sinh viên (BE Puppeteer render lại bản hiện tại).
            const profileCode = record?.profileCode || record?.code
            if (!profileCode) {
                Swal.fire({ text: 'Không tìm thấy mã hồ sơ để xuất PDF.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-light-danger' } })
                return
            }
            this.popupLoading()
            try {
                const { data } = await axios.get(`applicant/profile/${profileCode}`)
                if (!data || !data.id) {
                    Swal.close()
                    Swal.fire({ text: 'Chưa có hồ sơ nhập học để xuất PDF.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-light-danger' } })
                    return
                }
                const { data: pdf } = await axios.post(`/applicant/export/pdf/applicantinfo`, { id: data.id, studentCode: data.studentCode })
                Swal.close()
                if (pdf && pdf.filePath) {
                    window.open(pdf.filePath, '_blank')
                }
            } catch (error) {
                Swal.close()
                console.log('err', error)
                Swal.fire({ text: 'Không xuất được PDF. Vui lòng thử lại.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-light-danger' } })
            }
        },
    },
    async mounted() {
    },
    setup(props) {
        const kt_modal_lock_ad = ref<null | HTMLElement>(null);
        const lockAdModalRef = ref<null | HTMLElement>(null);
        const formData = ref<any>({
            programtype: "TIENG_VIET",
            reason: "",
            reasonError: "",
        })
        const router = useRouter()
        const optionsPrograms = ref<any>([
            {
                label: "Tiếng Việt",
                value: 'TIENG_VIET'
            }
        ])
        const optionsStatus = ref([
            { name: "Chọn nhập học", value: "opened", color: "success"},
            { name: "Đã hủy", value: "cancel", color: "danger"},
            { name: "Đã khóa", value: "none", color: "warning"}
        ])
        const tableHeader = ref([
            // MÃ HỒ SƠ đứng đầu (định danh chính, click mở drawer xem nhanh)
            {
                name: "MÃ HỒ SƠ",
                key: "profileCode",
                sortable: false,
                class: "min-w-60px"
            },
            {
                name: "NGÀY TẠO",
                key: "createdAt",
                sortable: true,
                class: "min-w-60px"
            },
            {
                name: "ĐỢT",
                key: "phase",
                sortable: true,
                class: "min-w-60px"
            },
            {
                name: "NGÀNH NHẬP HỌC",
                key: "major",
                sortable: true,
                class: "min-w-110px"
            },
            {
                name: "TÌNH TRẠNG",
                key: "status",
                sortable: true,
                class: "text-center"
            },
            {
                name: "XÁC NHẬN",
                key: "confirm",
                sortable: false,
                class: "text-center"
            },
            {
                name: "",
                key: "action",
                sortable: false,
                class: "text-end"
            }
        ]);
        const tableData = computed(() => {
            // Hồ sơ đang "Chọn nhập học" (opened) lên đầu, kế đến Đã khóa (còn thao tác được), Đã hủy cuối
            const rank = (s: string) => (s === 'opened' ? 0 : s === 'none' ? 1 : 2)
            return [...(props.dataApplicant || [])].sort((a: any, b: any) => rank(a.status) - rank(b.status))
        });
        
        return {
            router,
            optionsStatus,
            tableHeader,
            tableData,
            Permission,
            optionsPrograms,
            kt_modal_lock_ad,
            lockAdModalRef,
            formData,
        };
    },
});
